import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Button from '../../../../library/button/button';
import Tooltip from '../../../../library/tooltip/tooltip';
import Typography from '../../../../library/typography/typography';
import CoastCardBanner from '../../../../components/coast-card-banner/coast-card-banner';
import DTCCBanner from '../../../../components/dtcc-banner/dtcc-banner';
import GroupAffiliationPromotion from '../../../../components/group-affiliation-promotion/group-affiliation-promotion';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductFlags from '../../../../components/product-flags/product-flags';
import ProductImage from '../../../../components/product-image/product-image';
import ProductTitle from '../../../../components/product-title/product-title';
import ProductListCardPrice from '../../../product-list-page/components/product-list-card-price/product-list-card-price';
import ScrewdriverWrenchRegularIcon from '../../../../shared/icons/screwdriver-wrench-regular-icon';
import EasyPayOptions from '../easy-pay-options/easy-pay-options';

import {
  useAemContent,
  useMyAccount,
  useProductEntryDataLayerValues,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getStaggeredPdpUrl } from '../../../../shared/utils/cart/cart';
import { formatCurrency } from '../../../../shared/utils/format/format';
import { getProductImpressions } from '../../../../shared/utils/product/product';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import {
  BundleType,
  ProductData,
  PromotionData
} from '../../../../shared/types/graphql-types';
import { MapPrice } from '../../../../shared/types/product';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../../../shared/constants/cms';
import { PageTypes } from '../../../../shared/constants/page-types';

import styles from './pdp-deals-section.scss';

interface TopRecommendedProductData {
  frontProduct?: ProductData;
  rearProduct?: ProductData;
}

interface PdpDealsSectionProps {
  combinedMapPrice: MapPrice;
  isAddToCartDisabled: boolean;
  isCanonicalProduct: boolean;
  promos: Array<PromotionData>;
  topRecommendedProductData?: TopRecommendedProductData;
}

function PdpDealsSection(props: PdpDealsSectionProps) {
  const {
    combinedMapPrice,
    isAddToCartDisabled,
    isCanonicalProduct,
    promos,
    topRecommendedProductData
  } = props;

  const { isFleetAccount } = useMyAccount();
  const prevProductRef = useRef<ProductData | null>(null);
  const { pathname } = useLocation();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { aemTooltipsContent } = useAemContent(AEM_CONTENT_PAGE_IDS.global);
  const { quantity, rearQuantity } = useProductQuantity({
    bundleType: topRecommendedProductData?.rearProduct
      ? BundleType.StaggeredSet
      : undefined
  });
  const isFleetUser =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const inStockTooltip =
    aemTooltipsContent?.[AEM_CONTENT_IDS.inStockNearbyStore]?.tipText;
  const hasPromos = isArrayWithLength(promos);
  const showDtccBanner =
    !isCanonicalProduct && !isAddToCartDisabled && !hasPromos && !isFleetUser;

  const showCoastCard =
    !isCanonicalProduct && !isAddToCartDisabled && !hasPromos && isFleetUser;

  const { frontProduct, rearProduct } = topRecommendedProductData || {};
  const firstProduct = frontProduct || rearProduct;

  const topRecommendedProductPdp =
    frontProduct && rearProduct
      ? getStaggeredPdpUrl(frontProduct, rearProduct)
      : firstProduct?.url;

  const productsToTrack = useMemo(() => {
    if (!topRecommendedProductData) return null;

    if (frontProduct && rearProduct) {
      return {
        ...frontProduct,
        rearProduct
      };
    }

    return firstProduct || null;
  }, [frontProduct, rearProduct, firstProduct, topRecommendedProductData]);

  function handleRecommendedProductClick() {
    trackAction(
      'product_tw_oos_recommended_product_view_details_click',
      getProductEntryDataLayerValues(productsToTrack)
    );
  }

  useEffect(() => {
    if (productsToTrack && prevProductRef.current !== productsToTrack) {
      trackAction(
        'product_tw_oos_recommended_product_view',
        getProductImpressions(
          [productsToTrack].map(getProductEntryDataLayerValues),
          pathname
        )
      );
      prevProductRef.current = productsToTrack;
    }
  }, [productsToTrack, getProductEntryDataLayerValues, pathname]);

  return (
    <div className={styles['container']}>
      {!isFleetUser && (
        <GroupAffiliationPromotion
          className={styles['group-affiliation']}
          showTitle
        />
      )}
      {topRecommendedProductData && (
        <div className={styles['top-recommended-product-card']}>
          <div className={styles['top-recommended-product-card-banner']}>
            <ScrewdriverWrenchRegularIcon
              className={styles['screwdriver-wrench-icon']}
            />
            Shop <strong>in-stock tires</strong> for{' '}
            <strong>same-day install</strong>
            <Tooltip className={styles['tooltip']} content={inStockTooltip} />
          </div>

          <div className={styles['top-recommended-product-content']}>
            <ProductFlags
              className={styles['product-flags']}
              product={firstProduct}
            />

            <div className={styles['top-recommended-product-card-body']}>
              <ProductImage
                className={styles['product-image']}
                linkUrl={topRecommendedProductPdp}
                onClick={handleRecommendedProductClick}
                product={firstProduct}
              />

              <div className={styles['product-info']}>
                <ProductTitle
                  brand={firstProduct?.brand}
                  className={styles['product-title']}
                  link={topRecommendedProductPdp}
                  name={firstProduct?.name}
                  onClick={handleRecommendedProductClick}
                />

                <div className={styles['product-info-content']}>
                  {frontProduct && rearProduct ? (
                    <>
                      <div className={styles['product-info-section']}>
                        <div className={styles['product-price-and-size']}>
                          <Typography tag="span">
                            <Typography tag="span" weight="bold">
                              FRONT:
                            </Typography>{' '}
                            <Typography
                              className={styles['front-size']}
                              tag="span"
                            >
                              {frontProduct?.size}
                            </Typography>
                          </Typography>
                          <ProductListCardPrice
                            className={styles['product-price']}
                            hideFoundItLower
                            hidePercentageSaved
                            product={frontProduct}
                            quantity={quantity}
                          />
                        </div>
                        <ProductAvailability
                          className={styles['product-availability']}
                          product={frontProduct}
                          quantity={quantity}
                          rearProduct={rearProduct}
                          rearQuantity={rearQuantity}
                        />
                      </div>

                      <div className={styles['product-info-section']}>
                        <div className={styles['product-price-and-size']}>
                          <Typography tag="span">
                            <Typography tag="span" weight="bold">
                              REAR:
                            </Typography>{' '}
                            <Typography
                              className={styles['front-size']}
                              tag="span"
                            >
                              {rearProduct?.size}
                            </Typography>
                          </Typography>
                          <ProductListCardPrice
                            className={styles['product-price']}
                            hideFoundItLower
                            hidePercentageSaved
                            product={rearProduct}
                            quantity={quantity}
                          />
                        </div>
                        <ProductAvailability
                          className={styles['product-availability']}
                          product={frontProduct}
                          quantity={quantity}
                          rearProduct={rearProduct}
                          rearQuantity={rearQuantity}
                        />
                      </div>
                    </>
                  ) : (
                    <div className={styles['product-info-section']}>
                      <ProductListCardPrice
                        className={styles['product-price']}
                        hideFoundItLower
                        hidePercentageSaved
                        product={firstProduct}
                        quantity={quantity}
                      />
                      <ProductAvailability
                        className={styles['product-availability']}
                        product={firstProduct}
                        quantity={quantity}
                      />
                    </div>
                  )}

                  <Button
                    className={styles['product-details-link']}
                    href={topRecommendedProductPdp || ''}
                    onClick={handleRecommendedProductClick}
                  >
                    VIEW DETAILS
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <EasyPayOptions
        className={
          !topRecommendedProductData && isFleetUser ? styles['no-deals'] : ''
        }
      />
      <div className={styles['dtcc-banner']}>
        {showDtccBanner && (
          <DTCCBanner
            applyNowButtonId="prebuilt-package-product-block-apply-now"
            combinedMapPrice={combinedMapPrice}
            formattedValue={formatCurrency(combinedMapPrice?.price || 0)}
            pageType={PageTypes.PDP}
            value={combinedMapPrice?.price}
          />
        )}

        {showCoastCard && <CoastCardBanner />}
      </div>
    </div>
  );
}

export default PdpDealsSection;
